<template>
    <div>
        <div class="app-title">
            <em-view-title ref="emViewTitle" v-bind:crudState="$_emWorkflowTaskMixin_pathType"></em-view-title>
        </div>
        <section class="app-content">
            <div id="entity-viewer" class="content" v-menu-permission="'list'">
                <div class="card">
                    <div id="collapseOne" class="collapse show" aria-labelledby="headingOne"
                         data-parent="#accordionFilter">
                        <div class="card-body">
                            <em-workflow-business-data-editor :entity="modelName"
                                                              :allow-edit="false"
                                                              :business-data="$_emWorkflowTaskMixin_businessData">
                            </em-workflow-business-data-editor>
                        </div>
                    </div>
                </div>
            </div>
            <WorkflowProcessLine :processDefinitionKey="$_emWorkflowTaskMixin_processDefinitionKey"
                                 :config="config"
                                 ref="processExecution"
                                 @gotItems="setLocalVariables"
                                 :businessKey="$_emWorkflowTaskMixin_businessKey">
            </WorkflowProcessLine>
            <div id="em-workflow-task-complete" class="content" v-if="$_emWorkflowTaskMixin_isShowTaskComplete">
                <!--
                    三个 prop 缺一不可，缺了都是「不报错但流程走歪」：
                      complete-api      → 打业务侧 /emArchFixedAssetChange/completeTask，
                                          由 service 解析下一节点候选人（部门经理 / 财务）并刷新 changeType；
                                          不传就直接打工作流服务的 /completeTask，下一节点没有候选人。
                      need-pre-values   → 才会往 taskForm 里塞 fromTask / nextTask / taskUserKey /
                                          processInstanceBusinessKey，这四个正是上面那段解析的入参。
                      callback-api      → 业务侧 /emArchFixedAssetChange/complete-feedback（store 里配的），
                                          负责当前处理人 / 档案回写 / 通知；指到基类的
                                          /workflow/complete-feedback 只会更新状态。
                -->
                <WorkflowTaskComplete ref="processComplete"
                                      need-pre-values
                                      :complete-api="completeApi"
                                      :callback-api="$_emWorkflowTaskMixin_callbackApi"
                                      :task-id="$_emWorkflowTaskMixin_taskId"
                                      :businessKey="$_emWorkflowTaskMixin_businessKey"
                                      :status="$_emWorkflowTaskMixin_businessData.status"
                                      :config="config">
                </WorkflowTaskComplete>
            </div>
        </section>
    </div>
</template>

<script>
// 固定资产变更 | 审批页（验收 #8 UI）
// Author Liang Gangqiang
// Date 2026-09-07

import emWorkflowTaskMixin from "@/mixin/em/workflow/task";
import workflowTaskMixins from "@/components/workflow/mixin/WorkflowTaskMixins";
import WorkflowProcessLine from "@/components/workflow/stuffs/WorkflowProcessLine";
import WorkflowTaskComplete from "@/components/workflow/stuffs/WorkflowTaskComplete";

/**
 * 每个 config 的 key 必须与 BPMN em_arch_fixed_asset_change 里的 task id / endEvent id 一字不差：
 *   task_change_init / task_out_dept_manager_confirm / task_in_dept_manager_confirm /
 *   task_finance_confirm / Event_end_done / Event_end_cancel
 * （EmArchFixedAssetChangeStatus 是同一份字面量的 Java 侧枚举。）
 *
 * approved 的取值只有 1 / 2，与 BPMN 的出边条件 ${approved == 1} / ${approved == 2} 一一对应：
 *   1 = 提交 / 同意 / 是      2 = 结束流程 / 不同意 / 否
 * 注意是 Integer（WorkflowCompleteTaskForm.approved 是 @NotNull Integer），不是 yes/no 字符串。
 */
export default {
    name: "emArchFixedAssetChangeWorkflowTaskView",
    components: {WorkflowProcessLine, WorkflowTaskComplete},
    mixins: [emWorkflowTaskMixin, workflowTaskMixins],
    computed: {
        modelName() {
            return this.$_emRouterHelperMixin_entityName
        },

        completeApi() {
            return process.env.VUE_APP_EM_ARCHIVE_URL + '/emArchFixedAssetChange/completeTask';
        },

        /**
         * 当前单据的变更类型（库里的最新值）。退回后申请人可以在发起页改类型，
         * 所以每次进本页都要重新读，绝不能记在 data 里。
         */
        currentChangeType() {
            return this.$_emWorkflowTaskMixin_businessData
                    ? this.$_emWorkflowTaskMixin_businessData.changeType
                    : null;
        },

        /**
         * task_change_init「提交」之后的落点 = BPMN Gateway_change_type 的两条出边：
         *   COST_CENTER               → task_out_dept_manager_confirm
         *   ASSET_NAME/EQUIPMENT_CODE → task_finance_confirm
         * 前端必须自己算出来，否则 taskUserKey 会指到错的节点，下一节点就没有候选人。
         * 网关本身由 changeType 流程变量判断（后端 completeTask 每次都用库里最新值刷新它，
         * 本页的 addVariableByApprove 再带一份，双保险）。
         */
        nextTaskAfterInit() {
            return this.currentChangeType === 'COST_CENTER'
                    ? 'task_out_dept_manager_confirm'
                    : 'task_finance_confirm';
        },

        config() {
            return {
                // 兜底：下一节点没单独声明时按 <nextTaskId>_candidateUsers 组 taskUserKey。
                // 缺了这一行 getNextTaskUser() 会返回 null，getDefaultTaskUser() 直接抛 TypeError。
                defaultCandidateUser: true,

                'task_change_init': {
                    name: '发起变更申请',
                    variables: [
                        {
                            label: '流程操作',
                            type: this.VALUE_TYPE.SELECT,
                            saveTo: this.SAVE_TO.APPROVE,
                            options: [
                                {value: 1, text: '提交'},
                                {value: 2, text: '结束流程'}
                            ]
                        }
                    ],
                    withComment: true,
                    // 提交时把当前变更类型再写一次流程变量（URS R35；退回后可能已改）
                    addVariableByApprove: approved => {
                        return approved === 1 && this.currentChangeType
                                ? {name: 'changeType', value: this.currentChangeType}
                                : undefined;
                    },
                    approveToNextTask: {
                        1: this.nextTaskAfterInit,
                        2: 'Event_end_cancel'
                    },
                    // 本节点是 assignee 型（BPMN 里 activiti:assignee="#{task_change_init_assignee}"），
                    // 其余节点都是 candidateUsers。别的节点「不同意」退回到这里时，
                    // taskUserKey 要拼成 task_change_init_assignee 而不是 _candidateUsers。
                    defaultAssignee: true
                },

                'task_out_dept_manager_confirm': {
                    name: '转出部门经理审批',
                    variables: [
                        {
                            label: '审核结果',
                            type: this.VALUE_TYPE.SELECT,
                            saveTo: this.SAVE_TO.APPROVE,
                            options: [
                                {value: 1, text: '同意'},
                                {value: 2, text: '不同意'}
                            ]
                        }
                    ],
                    withComment: true,
                    approveToNextTask: {
                        1: 'task_in_dept_manager_confirm',
                        2: 'task_change_init'
                    }
                },

                'task_in_dept_manager_confirm': {
                    name: '转入部门经理审批',
                    variables: [
                        {
                            label: '审核结果',
                            type: this.VALUE_TYPE.SELECT,
                            saveTo: this.SAVE_TO.APPROVE,
                            options: [
                                {value: 1, text: '同意'},
                                {value: 2, text: '不同意'}
                            ]
                        }
                    ],
                    withComment: true,
                    approveToNextTask: {
                        1: 'task_finance_confirm',
                        2: 'task_change_init'
                    }
                },

                'task_finance_confirm': {
                    name: '财务人员确认变更',
                    variables: [
                        {
                            // 变量名 confirmResult 会作为流程变量一起提交；
                            // 入库的 confirm_result 由业务侧 complete-feedback 按 fromActivityId 写
                            // （SaveForm 里没有这个字段，走 PUT /selective 会被丢掉且触发全量校验）。
                            label: '确认变更',
                            name: 'confirmResult',
                            type: this.VALUE_TYPE.SELECT,
                            saveTo: this.SAVE_TO.APPROVE,
                            options: [
                                {value: 1, text: '是'},
                                {value: 2, text: '否'}
                            ]
                        }
                    ],
                    withComment: true,
                    approveToNextTask: {
                        1: 'Event_end_done',
                        2: 'task_change_init'
                    }
                },

                'Event_end_done': {
                    name: '变更完成',
                    isOver: true
                },
                'Event_end_cancel': {
                    name: '流程结束',
                    isOver: true
                }
            }
        }
    }
};
</script>
